import { useEffect, useState, useCallback, useContext } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import AlertService from "../../../services/AlertService";
import SupportService from "../../../services/SupportService";
import AuthContext from "../../../auth/AuthContext";
import Moment from "moment";
import AgentView from "./AgentView";
import { useNavigate, useLocation } from "react-router-dom";

const Ticket = () => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedClients, setSelectedClients] = useState(null);
  const [tickets, setTickets] = useState([]);
  const getInitialStatus = () => {
    const queryParams = new URLSearchParams(location.search);
    const statusParam = queryParams.get('status');
    return statusParam ? parseInt(statusParam) : 0;
  };
  const [status, setStatus] = useState(getInitialStatus); 
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [tableScrollHeight, setTableScrollHeight] = useState('400px');
  const ticketAccess = authContext.getPermissions("ticketdashboard");

  if (ticketAccess.filter((f) => f.access.includes(1)).length === 0) {
    navigate("/");
  }

  useEffect(() => {
    const calculateScrollHeight = () => {
      const windowHeight = window.innerHeight;
      let bannerHeight = 150;
      const otherFixedElementsHeight = 135;
      let calculatedHeight = windowHeight - bannerHeight - otherFixedElementsHeight;
      if (calculatedHeight < 200) {
        calculatedHeight = 200;
      }
      setTableScrollHeight(`${calculatedHeight}px`);
    };
    calculateScrollHeight();
    window.addEventListener('resize', calculateScrollHeight);
    return () => {
      window.removeEventListener('resize', calculateScrollHeight);
    };
  }, []);


const loadTickets = useCallback(async (signal) => { 
    setLoading(true);
    const skip = currentPage * rowsPerPage;
    const take = rowsPerPage;
    try {
      const data = await SupportService.GetTickets(
        status,
        skip,
        take,
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setTickets(data.data.records);
        setTotalRecords(data.data.totalRecords);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [status, currentPage, rowsPerPage, authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadTickets(controller.signal); 
    return () => controller.abort();
  }, [loadTickets]);


  const handleStatusChange = (val) => {
    setStatus(val);
    setCurrentPage(0);
    navigate(`/assigned-tickets/tickets?status=${val}`);
  };

  const statusBodyTemplate = (rowData) => {
    let color = "";
    switch (rowData.statusDesc) {
      case "In-Progress": color = "#0697DF"; break;
      case "Closed": color = "#017319"; break;
      case "Pending": color = "#FF5911"; break;
      case "Cancelled": color = "#D41B1B"; break;
      default: color = "#6c757d";
    }
    return <span style={{ color }}>{rowData.statusDesc}</span>;
  };

 const dateBodyTemplate = (rowData) => {
  if (loading) return <Skeleton width="100px" height="25px" />;

  if (!rowData?.ticketDate) return "-";

  return Moment.utc(rowData.ticketDate)
    .local()
    .format("DD/MM/YYYY hh:mm A");
};

  const generatePageNumbers = () => {
    const totalPages = Math.ceil(totalRecords / rowsPerPage);
    const currentPageNumber = currentPage + 1;
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPageNumber > maxVisiblePages + 1) pages.push("...");
      let start = Math.max(2, currentPageNumber - 1);
      let end = Math.min(totalPages - 1, currentPageNumber + 1);
      if (currentPageNumber <= maxVisiblePages) end = 4;
      else if (currentPageNumber > totalPages - maxVisiblePages) start = totalPages - 3;
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPageNumber < totalPages - maxVisiblePages) pages.push("...");
      if (totalPages > 1) pages.push(totalPages);
    }
    return pages;
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: rowsPerPage }).map((_, index) => ({
      ticketId: <Skeleton width="80%" />,
      courseName: <Skeleton width="90%" />,
      subject: <Skeleton width="95%" />,
      statusDesc: <Skeleton width="60%" />,
      ticketDate: <Skeleton width="70%" />
    }));
  };

  const statusHandler = (id, status, statusDesc) => {
    setTickets((prev) => {
      prev.filter((f) => f.id === id)[0].status = status;
      prev.filter((f) => f.id === id)[0].statusDesc = statusDesc;
      return [...prev];
    });
  };

  return (
    <div className="container-fluid custom-container position-relative">
      {!ticket ? (
        <>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mt-3">
            <div className="d-flex">
              <button className={`btn all-btn p-2 ${status === 0 ? 'active' : ''}`} onClick={() => handleStatusChange(0)}>
                All
              </button>
              <button className={`btn pending-btn p-2 ${status === 1 ? 'active' : ''}`} onClick={() => handleStatusChange(1)}>
                Pending
              </button>
              <button className={`btn inprogress-btn p-2 ${status === 2 ? 'active' : ''}`} onClick={() => handleStatusChange(2)}>
                In-Progress
              </button>
              <button className={`btn closed-btn p-2 ${status === 3 ? 'active' : ''}`} onClick={() => handleStatusChange(3)}>
                Closed
              </button>
              <button className={`btn cancelled-btn p-2 ${status === 4 ? 'active' : ''}`} onClick={() => handleStatusChange(4)}>
                Cancelled
              </button>
            </div>
          </div>
          <DataTable
            scrollable
            scrollHeight={tableScrollHeight}
            value={loading ? renderSkeletonRows() : tickets}
            selection={selectedClients}
            onSelectionChange={(e) => setSelectedClients(e.value)}
            className="custom-table"
            style={{ cursor: "pointer" }}
            onRowClick={(e) => {
              if (!loading && e.data) setTicket(e.data);
            }}
            rowClassName={(data) => (ticket?.id === data.id ? "selected-row" : "")}
          >
            <Column
              header="S. No."
              body={(_rowData, options) =>
                loading ? (
                  <div style={{ textAlign: "center", width: "100%" }}>
                    <Skeleton width="20px" height="1.5rem" />
                  </div>
                ) : (
                  <div style={{ textAlign: "center", width: "100%" }}>
                    {(currentPage * rowsPerPage) + options.rowIndex + 1}
                  </div>
                )
              }
              style={{ width: "10px" }}
              headerStyle={{ textAlign: "center" }}
            />
            <Column
              field="ticketId"
              header="Ticket ID"
              style={{ width: "200px" }}
              body={(rowData) =>
                loading ? <Skeleton width="80%" height="1.5rem" /> : rowData.ticketId
              }
            />
            <Column
              field="courseName"
              header="Course"
              style={{ width: "250px" }}
              body={(rowData) =>
                loading ? <Skeleton width="80%" height="1.5rem" /> : rowData.courseName
              }
            />
            <Column
              field="subject"
              header="Subject"
              style={{ width: "400px" }}
              body={(rowData) =>
                loading ? <Skeleton width="80%" height="1.5rem" /> : rowData.subject
              }
            />
            <Column
              field="statusDesc"
              header="Status"
              body={loading ? <Skeleton width="80%" height="1.5rem" /> : statusBodyTemplate}
              style={{ width: "120px" }}
            />
            <Column
              field="ticketDate"
              header="Request Date"
              body={loading ? <Skeleton width="80%" height="1.5rem" /> : dateBodyTemplate}
              style={{ width: "180px" }}
            />
          </DataTable>

          <div className="d-flex flex-column flex-start flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">
            <div className="d-flex align-items-center gap-3">

              <label className="">Show Rows:</label>
              <select
                className="form-select form-select-sm w-auto text-secondary custom-input-color"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value));
                  setCurrentPage(0);
                }}
              >
                {[20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
                 <span>
                Total Records: <span className="text-secondary">{totalRecords}</span>
              </span>
            </div>
            <div className="d-flex justify-content-center align-items-center gap-2">
              <button
                className="btn p-0 d-flex align-items-center justify-content-center fs-5 pagination-item"
                style={{ width: "40px", height: "40px" }}
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
              >
                &lt;
              </button>

              {generatePageNumbers().map((page, index) =>
                page === "..." ? (
                  <span key={index} className="mx-1 text-secondary">...</span>
                ) : (
                  <button
                    key={index}
                    className={`btn p-0 d-flex align-items-center justify-content-center text-secondary pagination-item ${currentPage + 1 === page ? "active-page" : ""}`}
                    style={{ width: "40px", height: "40px" }}
                    onClick={() => setCurrentPage(page - 1)}
                  >
                    {page}
                  </button>
                )
              )}

              <button
                className="btn p-0 d-flex align-items-center justify-content-center fs-5 pagination-item"
                style={{ width: "40px", height: "40px" }}
                onClick={() =>
                  setCurrentPage(Math.min(Math.ceil(totalRecords / rowsPerPage) - 1, currentPage + 1))
                }
                disabled={currentPage >= Math.ceil(totalRecords / rowsPerPage) - 1}
              >
                &gt;
              </button>
            </div>
          </div>
        </>
      ) : (
        <AgentView
          ticket={ticket}
          clearSelection={() => setTicket(null)}
          loadTickets={loadTickets}
          statusHandler={statusHandler}
        />
      )}
    </div>
  );
};

export default Ticket;
